<!-- ABOUTME: Vue component for displaying broadcast email details with tabbed language variants -->
<!-- ABOUTME: Shows 1:1 email preview matching actual sent emails, uses Tailwind CSS exclusively -->
<template>
  <div class="max-w-6xl mx-auto px-4 py-6">
    <!-- Compact Header -->
    <div class="flex justify-between items-center mb-6 pb-4 border-b border-gray-200">
      <div class="flex items-center gap-4">
        <h1 class="text-2xl font-bold text-gray-900">{{ emailContent.title || 'Untitled Broadcast' }}</h1>
        <span :class="statusBadgeClass">{{ statusText }}</span>
      </div>
      <div class="flex gap-3">
        <button @click="duplicateBroadcast" class="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-md transition-colors">
          <Copy :size="16" class="mr-2" />
          Duplicate
        </button>
        <a :href="indexUrl" class="inline-flex items-center px-4 py-2 bg-primary-color hover:bg-teal-600 text-white text-sm font-medium rounded-md transition-colors">
          Back to List
        </a>
      </div>
    </div>

    <!-- Compact Details Grid -->
    <div class="bg-white border border-gray-200 rounded-lg p-4 mb-5">
      <div class="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div class="flex items-baseline gap-2">
          <span class="text-xs font-semibold text-gray-500 uppercase tracking-wide">Created:</span>
          <span class="text-sm text-gray-900 font-medium">{{ formatDate(emailContent.created_at) }}</span>
        </div>
        <div v-if="emailContent.sent_at" class="flex items-baseline gap-2">
          <span class="text-xs font-semibold text-gray-500 uppercase tracking-wide">Sent:</span>
          <span class="text-sm text-gray-900 font-medium">{{ formatDate(emailContent.sent_at) }}</span>
        </div>
        <div class="flex items-baseline gap-2">
          <span class="text-xs font-semibold text-gray-500 uppercase tracking-wide">Languages:</span>
          <span class="text-sm font-semibold text-primary-color">
            <span v-for="(variant, index) in emailContent.variants" :key="variant.locale">
              {{ variant.locale.toUpperCase() }}<span v-if="index < emailContent.variants.length - 1">, </span>
            </span>
          </span>
        </div>
      </div>
    </div>

    <!-- Recipients Section -->
    <div v-if="recipientCount !== null" class="bg-white border border-gray-200 rounded-lg p-4 mb-5">
      <div class="flex justify-between items-center">
        <h3 class="flex items-center text-base font-semibold text-gray-900">
          <Users :size="18" class="mr-2 text-primary-color" />
          Recipients
          <span class="ml-2 px-2.5 py-0.5 bg-primary-color text-white text-xs font-semibold rounded-full">{{ recipientCount }}</span>
        </h3>
        <button @click="toggleRecipients" class="flex items-center gap-1.5 px-4 py-2 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-md text-sm font-medium text-gray-700 transition-colors">
          <ChevronDown v-if="!showRecipients" :size="18" />
          <ChevronUp v-else :size="18" />
          {{ showRecipients ? 'Hide' : 'Show' }}
        </button>
      </div>

      <div v-if="showRecipients" class="mt-4 pt-4 border-t border-gray-200">
        <div v-if="loadingRecipients" class="py-6 text-center text-sm text-gray-500">
          Loading recipients...
        </div>
        <div v-else-if="recipients.length > 0" class="flex flex-col gap-2">
          <div v-for="recipient in recipients" :key="recipient.id" class="flex items-center gap-4 px-3 py-2 bg-gray-50 hover:bg-gray-100 rounded-md transition-colors">
            <span class="flex-1 text-sm font-medium text-gray-900">{{ recipient.email }}</span>
            <span v-if="recipient.name" class="text-sm text-gray-600">{{ recipient.name }}</span>
            <span class="text-xs text-gray-400 whitespace-nowrap">{{ formatRelativeTime(recipient.sent_at) }}</span>
          </div>
        </div>
        <div v-else class="py-6 text-center text-sm text-gray-500">
          No recipients found
        </div>
      </div>
    </div>

    <!-- Email Preview with Tabs -->
    <div class="bg-white border border-gray-200 rounded-lg overflow-hidden">
      <div class="bg-gray-50 border-b border-gray-200 p-4">
        <h2 class="flex items-center text-lg font-semibold text-gray-900 mb-4">
          <Mail :size="20" class="mr-2 text-primary-color" />
          Email Preview
        </h2>
        <div class="flex gap-2 flex-wrap">
          <button
            v-for="variant in emailContent.variants"
            :key="variant.locale"
            @click="activeTab = variant.locale"
            :class="[
              'px-5 py-2 text-sm font-medium rounded-md transition-colors',
              activeTab === variant.locale
                ? 'bg-primary-color text-white'
                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
            ]"
          >
            {{ getLanguageName(variant.locale) }}
          </button>
        </div>
      </div>

      <div class="p-6">
        <div v-for="variant in emailContent.variants"
             :key="variant.locale"
             v-show="activeTab === variant.locale"
             class="animate-fade-in">

          <!-- Subject -->
          <div class="mb-6">
            <div class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Subject</div>
            <div class="text-base font-semibold text-gray-900 py-2">{{ renderSubject(variant) }}</div>
          </div>

          <!-- Email Body - 1:1 with actual email (white background, no gray) -->
          <div class="mb-2">
            <div class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Email Body</div>
          </div>
          <div class="email-body-preview" v-html="renderBody(variant)"></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { Copy, Users, ChevronDown, ChevronUp, Mail } from 'lucide-vue-next'
import axios from 'axios'

const props = defineProps({
  emailContent: { type: Object, required: true },
  indexUrl: { type: String, required: true },
  duplicateUrl: { type: String, required: true },
  recipientsUrl: { type: String, default: null }
})

const showRecipients = ref(false)
const recipients = ref([])
const loadingRecipients = ref(false)
const recipientCount = ref(null)
const activeTab = ref(props.emailContent.variants[0]?.locale || 'en')

const sampleVariables = {
  first_name: 'John',
  last_name: 'Doe',
  email: 'john.doe@example.com',
  network_url: 'https://unlisters.com/network',
  projects_url: 'https://unlisters.com/projects',
  login_url: 'https://unlisters.com/login'
}

const languageNames = {
  en: 'English',
  sk: 'Slovak',
  cs: 'Czech',
  hu: 'Hungarian'
}

const statusText = computed(() => {
  return props.emailContent.status.charAt(0).toUpperCase() + props.emailContent.status.slice(1)
})

const statusBadgeClass = computed(() => {
  const baseClasses = 'inline-block px-3 py-1 rounded-md text-xs font-semibold uppercase tracking-wide'
  const statusClasses = {
    draft: 'bg-yellow-100 text-yellow-800',
    sent: 'bg-green-100 text-green-800',
    scheduled: 'bg-blue-100 text-blue-800',
    archived: 'bg-gray-100 text-gray-600'
  }
  return `${baseClasses} ${statusClasses[props.emailContent.status] || 'bg-gray-100 text-gray-600'}`
})

function getLanguageName(locale) {
  return languageNames[locale] || locale.toUpperCase()
}

function formatDate(dateString) {
  if (!dateString) return ''
  const date = new Date(dateString)
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

function formatRelativeTime(dateString) {
  if (!dateString) return ''
  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now - date
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  return `${diffDays}d ago`
}

function renderSubject(variant) {
  let subject = variant.subject
  Object.keys(sampleVariables).forEach(key => {
    const placeholder = `{{${key}}}`
    subject = subject.replace(new RegExp(placeholder, 'g'), sampleVariables[key])
  })
  return subject
}

function renderBody(variant) {
  let body = variant.body

  Object.keys(sampleVariables).forEach(key => {
    const placeholder = `{{${key}}}`
    body = body.replace(new RegExp(placeholder, 'g'), sampleVariables[key])
  })

  body = body.replace(/\{\{button:(primary|secondary|success|danger):(.+?):([^:}]+)\}\}/g, (match, style, url, text) => {
    return generateEmailButton(text, url, style)
  })

  body = body.replace(/\{\{link:(.+?):([^:}]+)\}\}/g, (match, url, text) => {
    return generateEmailLink(text, url)
  })

  return body
}

function generateEmailButton(text, url, style = 'primary') {
  const colors = {
    primary: { bg: '#17B7B7', text: '#ffffff' },
    secondary: { bg: '#2271B1', text: '#ffffff' },
    success: { bg: '#10b981', text: '#ffffff' },
    danger: { bg: '#ef4444', text: '#ffffff' }
  }

  const color = colors[style] || colors.primary

  return `
    <table role="presentation" border="0" cellpadding="0" cellspacing="0" style="border-collapse: separate; width: auto; margin: 20px 0;">
      <tr>
        <td style="border-radius: 6px; background-color: ${color.bg}; text-align: center;">
          <a href="${url}" target="_blank" style="display: inline-block; padding: 12px 24px; font-family: sans-serif; font-size: 14px; font-weight: 600; color: ${color.text}; text-decoration: none; border-radius: 6px;">${text}</a>
        </td>
      </tr>
    </table>
  `
}

function generateEmailLink(text, url) {
  return `<a href="${url}" style="color: #17B7B7; text-decoration: none; font-weight: 500;">${text}</a>`
}

async function toggleRecipients() {
  showRecipients.value = !showRecipients.value
  if (showRecipients.value && recipients.value.length === 0 && props.recipientsUrl) {
    await loadRecipients()
  }
}

async function loadRecipients() {
  if (!props.recipientsUrl) return
  loadingRecipients.value = true

  try {
    const response = await axios.get(props.recipientsUrl)
    recipients.value = response.data.recipients || []
    recipientCount.value = response.data.count || recipients.value.length
  } catch (error) {
    console.error('Failed to load recipients:', error)
  } finally {
    loadingRecipients.value = false
  }
}

async function duplicateBroadcast() {
  if (!confirm('Create a new draft broadcast with the same content?')) return

  try {
    const response = await axios.post(props.duplicateUrl, {}, {
      headers: {
        'X-CSRF-Token': getCsrfToken(),
        'Accept': 'application/json'
      }
    })

    if (response.data.success) {
      window.location.href = response.data.redirect_url
    } else {
      alert('Failed to duplicate broadcast email')
    }
  } catch (error) {
    console.error('Failed to duplicate broadcast:', error)
    alert('Error duplicating broadcast email')
  }
}

function getCsrfToken() {
  const meta = document.querySelector('meta[name="csrf-token"]')
  return meta ? meta.content : ''
}

onMounted(async () => {
  if (props.recipientsUrl) {
    try {
      const response = await axios.get(props.recipientsUrl + '?count_only=true')
      recipientCount.value = response.data.count || 0
    } catch (error) {
      console.error('Failed to load recipient count:', error)
    }
  }
})
</script>

<style scoped>
/* Minimal scoped styles - only for 1:1 email preview rendering */

/* Fade animation for tab switching */
.animate-fade-in {
  animation: fadeIn 0.2s ease-in;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

/* Email body preview - matches EXACTLY what's sent (white background, no border) */
.email-body-preview {
  background: white;
  font-family: Arial, sans-serif;
  line-height: 1.6;
  color: #333;
  font-size: 14px;
  padding: 0;
  margin: 0;
}

/* Paragraph spacing matches email layout */
.email-body-preview :deep(p) {
  padding: 0;
  margin: 0 0 16px 0;
}

.email-body-preview :deep(p:last-child) {
  margin-bottom: 0;
}

/* List styling */
.email-body-preview :deep(ul),
.email-body-preview :deep(ol) {
  margin: 12px 0;
  padding-left: 24px;
}

.email-body-preview :deep(li) {
  margin: 4px 0;
}

/* Button styling - WHITE TEXT, NO UNDERLINE, NO HOVER CHANGE */
.email-body-preview :deep(table a) {
  color: #ffffff !important;
  text-decoration: none !important;
  font-weight: 600 !important;
}

.email-body-preview :deep(table a:hover) {
  color: #ffffff !important;
  text-decoration: none !important;
}

/* Link styling (not in buttons) */
.email-body-preview :deep(a:not(table a)) {
  color: #17B7B7;
  text-decoration: none;
  font-weight: 500;
}

.email-body-preview :deep(table) {
  margin: 20px 0;
}

/* Tailwind custom color for primary */
.bg-primary-color {
  background-color: #17B7B7;
}

.text-primary-color {
  color: #17B7B7;
}

.hover\:bg-teal-600:hover {
  background-color: #0fa8a8;
}

/* Mobile responsive */
@media (max-width: 768px) {
  .grid-cols-2 {
    grid-template-columns: 1fr;
  }
}
</style>
